import { loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { enumAttribute, numberAttribute, primitiveStyles } from '../shared/primitive-utils.js';
import { statusSeverities, statusStyles, type StatusSeverity } from '../shared/status-styles.js';

/**
 * Compact count or text status. Dot mode requires a meaningful label.
 * @element qds-badge
 * @attr {number} count - Optional nonnegative count; omitting it uses slotted text.
 * @attr {number} max - Count overflow threshold. Default 99.
 * @attr {boolean} dot - Compact status dot, with label available to assistive technology.
 * @attr {string} label - Count suffix or dot status, e.g. unread messages.
 * @attr {'neutral'|'info'|'success'|'warning'|'error'} severity - Status color.
 * @property {number|null} count - Count or null for text content.
 * @property {number} max - Overflow threshold.
 * @property {string} severity - Semantic status.
 * @slot - Text content when count and dot are omitted.
 * @csspart badge - Status surface.
 */
export class QdsBadge extends HTMLElement {
  static get observedAttributes(): string[] { return ['count', 'max', 'dot', 'label', 'severity']; }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}${statusStyles}
      :host { display: inline-flex; vertical-align: middle; }
      [part="badge"] { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; min-height: 20px; padding: 0 6px;
        border-radius: 999px; font-size: 12px; font-weight: 600; background: var(--_status-bg); color: var(--_status-fg); }
      :host([dot]) [part="badge"] { min-width: 8px; min-height: 8px; width: 8px; height: 8px; padding: 0; }
      .sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
      @media (forced-colors: active) { [part="badge"] { border: 1px solid CanvasText; } }
    </style><span part="badge"><slot></slot><span class="count" aria-hidden="true"></span><span class="sr"></span></span>`;
    loadBaseCSS();
  }
  connectedCallback(): void { for (const prop of ['count', 'max', 'severity']) upgradeProperty(this, prop); this._sync(); }
  attributeChangedCallback(): void { if (this.isConnected) this._sync(); }
  get count(): number | null { const value = numberAttribute(this, 'count', NaN); return Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : null; }
  set count(value: number | null) { if (value == null) this.removeAttribute('count'); else this.setAttribute('count', String(value)); }
  get max(): number { return Math.max(0, Math.trunc(numberAttribute(this, 'max', 99))); }
  set max(value: number) { this.setAttribute('max', String(value)); }
  get severity(): StatusSeverity { return enumAttribute(this, 'severity', statusSeverities, 'neutral'); }
  set severity(value: StatusSeverity) { this.setAttribute('severity', value); }
  private _sync(): void {
    const dot = this.hasAttribute('dot');
    const count = this.count;
    const label = this.getAttribute('label') || '';
    this.shadowRoot!.querySelector<HTMLSlotElement>('slot')!.hidden = dot || count !== null;
    this.shadowRoot!.querySelector('.count')!.textContent = dot || count === null ? '' : count > this.max ? `${this.max}+` : String(count);
    this.shadowRoot!.querySelector('.sr')!.textContent = dot ? label || 'Status' : count === null ? '' : `${count}${label ? ` ${label}` : ''}`;
  }
}
if (!customElements.get('qds-badge')) customElements.define('qds-badge', QdsBadge);
